import { useQuery } from '@apollo/client';
import { Card, Container, LinearProgress, Stack, Typography } from '@mui/material';
import { MetadataItem } from 'components/common/metadata';
import { MetadataItemCopy } from 'components/common/metadata/metadata-item';
import { TcrCard } from 'components/views/campaign/vendors/tcr';
import { useDemoMode } from 'providers/demo-mode';
import { FC, useState } from 'react';
import { useParams } from 'react-router';
import {
  CampaignVendors,
  CampaignVendors_campaign,
  CampaignVendorsVariables,
} from 'types/apollo/CampaignVendors';
import { A2pCards } from './a2p';
import { CampaignIvrUrlsCard } from './campaign-ivr-urls-card';
import { ProvisionCard } from './provision-card';
import { VendorSummary } from './vendor-summary';
import { CampaignVendorsQuery } from './vendors.graphql';

export const Vendors: FC = () => {
  const { isInDemoMode } = useDemoMode();
  const { id, campaignId } = useParams() as { id?: string; campaignId: string };
  const [state, setState] = useState<Omit<CampaignVendors_campaign, '__typename'>>({
    id: '',
    vendorAccounts: [],
    twilioSubAccount: null,
    transferPhoneOutbound: null,
    transferPhoneInbound: null,
    ivrAudioUrls: null,
    agentPhones: [],
  });

  const { loading, refetch } = useQuery<CampaignVendors, CampaignVendorsVariables>(
    CampaignVendorsQuery,
    {
      variables: { campaignId },
      onCompleted: (data) => {
        if (data.campaign) {
          setState(data.campaign);
        }
      },
    },
  );

  return (
    <Container
      maxWidth="lg"
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      {!isInDemoMode && (
        <Card variant="outlined" sx={{ padding: 2 }}>
          {loading && <LinearProgress sx={{ height: '2px', mb: 1 }} />}
          <Typography variant="h4">Twilio</Typography>
          <MetadataItem
            label="Twilio Subaccount"
            value={state?.twilioSubAccount?.friendlyName ?? 'Not setup yet'}
          />
          <MetadataItemCopy
            label="Twilio SID"
            value={state?.twilioSubAccount?.twilioAccountSid ?? 'Not setup yet'}
          />
          <Typography variant="h4">Transfer phones</Typography>
          <Stack direction="row" sx={{ minWidth: 400, maxWidth: '40%' }}>
            <MetadataItem label="Inbound" value={state?.transferPhoneInbound ?? 'Not setup yet'} />
            <MetadataItem
              label="Outbound"
              value={state?.transferPhoneOutbound ?? 'Not setup yet'}
            />
          </Stack>
          <Typography variant="h4">Volumes</Typography>
          <Stack direction="row" sx={{ minWidth: 400, maxWidth: '50%' }}>
            <VendorSummary vendors={state?.vendorAccounts} />
          </Stack>
        </Card>
      )}
      {!isInDemoMode &&
        (state.id ? (
          <CampaignIvrUrlsCard
            campaignId={state.id}
            companyId={id}
            inboundIvrAudioUrl={state.ivrAudioUrls?.inbound ?? null}
            scheduledIvrAudioUrl={state.ivrAudioUrls?.scheduled ?? null}
            unscheduledIvrAudioUrl={state.ivrAudioUrls?.unscheduled ?? null}
            onSaved={() => refetch()}
          />
        ) : (
          loading && (
            <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
              <Typography variant="h4">IVR Audio URLs</Typography>
              <LinearProgress sx={{ height: '2px', mt: 1 }} />
            </Card>
          )
        ))}
      {!isInDemoMode &&
        (state.id ? (
          <TcrCard campaignId={campaignId} internalCampaignId={state.id} />
        ) : (
          loading && (
            <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
              <Typography variant="h4">TCR Campaign</Typography>
              <LinearProgress sx={{ height: '2px', mt: 1 }} />
            </Card>
          )
        ))}
      <ProvisionCard campaign={state} loading={loading} onProvisioned={() => refetch()} />
      {state.id ? (
        <A2pCards campaignId={campaignId} internalCampaignId={state.id} />
      ) : (
        loading && (
          <Card variant="outlined" sx={{ padding: 2, marginTop: 2 }}>
            <Typography variant="h5">A2P registration</Typography>
            <LinearProgress sx={{ height: '2px', mt: 1 }} />
          </Card>
        )
      )}
    </Container>
  );
};
